import React, { useEffect, useRef, useState } from 'react'
import type { Meta, Story } from '@tntd/storybook-react-storykit'
import Form from './index'
import type { FormProps } from './Form'
import {
  Input,
  InputNumber,
  Checkbox,
  Button,
  Select,
  Radio,
  Row,
  message,
  Icon,
  Modal,
  Drawer,
  Cascader,
  DatePicker,
  TimePicker,
  TreeSelect,
  Switch,
  Upload,
} from '../index'
import type { Input as AntdInput } from 'antd'
import moment from 'moment'
import type { SelectProps } from '../select'

const { Option } = Select
const options = [
  { label: 'Apple', value: 'apple' },
  { label: 'Pear', value: 'pear' },
]

export default {
  title: 'TntdForm',
  component: Form,
} as Meta<typeof Form>

//👇 We create a “template” of how args map to rendering
const Template: Story<FormProps<any>> = (args) => {
  return <Form {...args} />
}

export const Basic = Template.bind({})

Basic.args = {
  //👇 The args you need here will depend on your component
  initialValues: {
    username: 'zhang san',
    remember: true,
  },
  name: 'Basic',
  disabled: false,
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
  onFinish: (values) => {
    console.log('Success:', values)
  },
  onFinishFailed(errorInfo) {
    console.log('Failed:', errorInfo)
  },
  autoComplete: 'off',
  children: (
    <>
      <Form.Item
        label="Username"
        name="username"
        rules={[{ required: true, message: 'Please input your username!' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: 'Please input your password!' }]}
      >
        <Input.Password />
      </Form.Item>

      <Form.Item name="remember" valuePropName="checked" wrapperCol={{ offset: 8, span: 16 }}>
        <Checkbox>Remember me</Checkbox>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </>
  ),
}

const FormMethodsTemplate: Story<FormProps<any>> = (args) => {
  const [form] = Form.useForm()

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }
  const tailLayout = {
    wrapperCol: { offset: 8, span: 16 },
  }

  const onGenderChange: SelectProps['onChange'] = (value) => {
    switch (value) {
      case 'male':
        form.setFieldsValue({ note: 'Hi, man!' })
        return
      case 'female':
        form.setFieldsValue({ note: 'Hi, lady!' })
        return
      case 'other':
        form.setFieldsValue({ note: 'Hi there!' })
    }
  }

  const onFinish = (values: any) => {
    console.log(values)
  }

  const onReset = () => {
    form.resetFields()
  }

  const onFill = () => {
    form.setFieldsValue({
      note: 'Hello world!',
      gender: 'male',
    })
  }

  return (
    <Form {...layout} form={form} name="control-hooks" onFinish={onFinish} {...args}>
      <Form.Item name="note" label="Note" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
        <Select
          placeholder="Select a option and change input text above"
          onChange={onGenderChange}
          allowClear
        >
          <Option value="male">male</Option>
          <Option value="female">female</Option>
          <Option value="other">other</Option>
        </Select>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.gender !== currentValues.gender}
      >
        {({ getFieldValue }) =>
          getFieldValue('gender') === 'other' ? (
            <Form.Item name="customizeGender" label="Customize Gender" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Button type="primary" htmlType="submit" style={{ marginRight: 12 }}>
          Submit
        </Button>
        <Button htmlType="button" onClick={onReset}>
          Reset
        </Button>
        <Button type="link" htmlType="button" onClick={onFill}>
          Fill form
        </Button>
      </Form.Item>
    </Form>
  )
}

// 表单方法调用
export const FormMethodInvoke = FormMethodsTemplate.bind({})

FormMethodInvoke.args = {}

// 表单布局
type LayoutType = Parameters<typeof Form>[0]['layout']
const FormLayoutTemplate = () => {
  const [form] = Form.useForm()
  const [formLayout, setFormLayout] = useState<LayoutType>('horizontal')

  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout)
  }

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null

  return (
    <Form
      {...formItemLayout}
      layout={formLayout}
      form={form}
      initialValues={{ layout: formLayout }}
      onValuesChange={onFormLayoutChange}
    >
      <Form.Item label="Form Layout" name="layout">
        <Radio.Group value={formLayout}>
          <Radio.Button value="horizontal">Horizontal</Radio.Button>
          <Radio.Button value="vertical">Vertical</Radio.Button>
          <Radio.Button value="inline">Inline</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item label="Field A">
        <Input placeholder="input placeholder" />
      </Form.Item>
      <Form.Item label="Field B">
        <Input placeholder="input placeholder" />
      </Form.Item>
      <Form.Item {...buttonItemLayout}>
        <Button type="primary">Submit</Button>
      </Form.Item>
    </Form>
  )
}

export const FormLayoutDemo = FormLayoutTemplate.bind({})

// 非阻塞校验
const UnblockValidateTemplate = () => {
  const [form] = Form.useForm()

  const onFinish = () => {
    void message.success('Submit success!')
  }

  const onFinishFailed = () => {
    void message.error('Submit failed!')
  }

  const onFill = () => {
    form.setFieldsValue({
      url: 'https://taobao.com/',
    })
  }

  return (
    <Form form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off">
      <Form.Item
        name="url"
        label="URL"
        rules={[{ required: true }, { type: 'url', warningOnly: true }, { type: 'string', min: 6 }]}
      >
        <Input placeholder="input placeholder" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" style={{ marginRight: 12 }}>
          Submit
        </Button>
        <Button htmlType="button" onClick={onFill}>
          Fill
        </Button>
      </Form.Item>
    </Form>
  )
}

export const UnblockValidate = UnblockValidateTemplate.bind({})

// 字段监听hooks
const UseWatchTemplate = () => {
  const [form] = Form.useForm<{ name: string; age: number }>()
  const nameValue = Form.useWatch('name', form)

  return (
    <>
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item name="name" label="Name (Watch to trigger rerender)">
          <Input />
        </Form.Item>
        <Form.Item name="age" label="Age (Not Watch)">
          <InputNumber />
        </Form.Item>
      </Form>

      <pre>Name Value: {nameValue}</pre>
    </>
  )
}

export const UseWatchHooks = UseWatchTemplate.bind({})

// 动态增减标单项
const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 20 },
  },
}
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 4 },
  },
}
const DynamicAddRemoveFieldsTpl = () => {
  const onFinish = (values: any) => {
    console.log('Received values of form:', values)
  }

  return (
    <Form name="dynamic_form_item" {...formItemLayoutWithOutLabel} onFinish={onFinish}>
      <Form.List
        name="names"
        rules={[
          {
            validator: async (_, names) => {
              if (!names || names.length < 2) {
                return Promise.reject(new Error('At least 2 passengers'))
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Form.Item
                {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                label={index === 0 ? 'Passengers' : ''}
                required={false}
                key={field.key}
              >
                <Form.Item
                  {...field}
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Please input passenger's name or delete this field.",
                    },
                  ]}
                  noStyle
                >
                  <Input placeholder="passenger name" style={{ width: '60%' }} />
                </Form.Item>
                {fields.length > 1 ? (
                  <Icon
                    type="minus-circle"
                    className="dynamic-delete-button"
                    onClick={() => remove(field.name)}
                  />
                ) : null}
              </Form.Item>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                style={{ width: '60%' }}
                icon="plus-circle"
              >
                Add field
              </Button>
              <Button
                type="dashed"
                onClick={() => {
                  add('The head item', 0)
                }}
                style={{ width: '60%', marginTop: '20px' }}
                icon="plus-circle"
              >
                Add field at head
              </Button>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

export const DynamicAddRemoveFields = DynamicAddRemoveFieldsTpl.bind({})

const areas = [
  { label: 'Beijing', value: 'Beijing' },
  { label: 'Shanghai', value: 'Shanghai' },
]

const sights = {
  Beijing: ['Tiananmen', 'Great Wall'],
  Shanghai: ['Oriental Pearl', 'The Bund'],
}

type SightsKeys = keyof typeof sights

const DynamicItemsComplexTpl = () => {
  const [form] = Form.useForm()

  const onFinish = (values: any) => {
    console.log('Received values of form:', values)
  }

  const handleChange = () => {
    form.setFieldsValue({ sights: [] })
  }

  return (
    <Form form={form} name="dynamic_form_complex" onFinish={onFinish} autoComplete="off">
      <Form.Item name="area" label="Area" rules={[{ required: true, message: 'Missing area' }]}>
        <Select onChange={handleChange}>
          {areas.map(({ label, value }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.List name="sights">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Row key={field.key} style={{ display: 'flex' }}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                  }
                >
                  {() => (
                    <Form.Item
                      {...field}
                      label="Sight"
                      name={[field.name, 'sight']}
                      rules={[{ required: true, message: 'Missing sight' }]}
                    >
                      <Select disabled={!form.getFieldValue('area')} style={{ width: 130 }}>
                        {(sights[form.getFieldValue('area') as SightsKeys] || []).map((item) => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Form.Item>
                <Form.Item
                  {...field}
                  label="Price"
                  name={[field.name, 'price']}
                  rules={[{ required: true, message: 'Missing price' }]}
                >
                  <Input />
                </Form.Item>

                <Icon type="minus-circle" onClick={() => remove(field.name)} />
              </Row>
            ))}

            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon="plus-circle">
                Add sights
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

export const DynamicItemsComplex = DynamicItemsComplexTpl.bind({})

// 弹窗中表单
const FormInModalTpl = () => {
  const [data, setData] = useState<FormProps['initialValues'] | null>({})
  const [popupType, setPopupType] = useState<'modal' | 'drawer'>('modal')
  const PopupCom = {
    modal: Modal,
    drawer: Drawer,
  }[popupType]

  const close = () => setData(null)

  return (
    <>
      <Button type="primary" onClick={() => setData({})} style={{ marginRight: 12 }}>
        新建
      </Button>
      <Button
        type="primary"
        style={{ marginRight: 12, marginBottom: 12 }}
        onClick={() =>
          setData({
            id: 1,
            name: 'Hello',
            desc: 'Hello World',
          })
        }
      >
        编辑
      </Button>
      <br />
      <Radio.Group value={popupType} onChange={(evt) => setPopupType(evt.target.value)}>
        <Radio.Button value="modal">弹窗</Radio.Button>
        <Radio.Button value="drawer">抽屉</Radio.Button>
      </Radio.Group>
      <PopupCom
        title={`${data?.id ? '编辑' : '新建'}`}
        visible={!!data}
        width="40%"
        destroyOnClose
        onClose={close}
        onCancel={close}
        onOk={close}
      >
        <Form initialValues={data!}>
          <Form.Item name="name" label="名称">
            <Input />
          </Form.Item>
          <Form.Item name="desc" label="描述">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </PopupCom>
    </>
  )
}

export const FormInModal = FormInModalTpl.bind({})

// 表单禁用/只读
const { RangePicker, MonthPicker, WeekPicker } = DatePicker
const { TextArea } = Input

const FormDisabledTpl = () => {
  const [componentDisabled, setComponentDisabled] = useState<boolean>(true)
  const [componentReadonly, setComponentReadonly] = useState<boolean>(false)
  const inputRef = useRef<AntdInput>(null)
  const onFormLayoutChange = ({ disabled, ...values }: { disabled: boolean }) => {
    console.log(values)
    setComponentDisabled(disabled)
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [componentDisabled, componentReadonly])

  return (
    <>
      <Checkbox
        checked={componentDisabled}
        onChange={(e) => setComponentDisabled(e.target.checked)}
      >
        Form disabled
      </Checkbox>
      <Checkbox
        checked={componentReadonly}
        onChange={(e) => setComponentReadonly(e.target.checked)}
      >
        Form readonly
      </Checkbox>
      <Form
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        onValuesChange={onFormLayoutChange}
        disabled={componentDisabled}
        readonly={componentReadonly}
        initialValues={{
          disabled: true,
          switch: true,
          input: 'Hello World',
          select: 'demo',
          select2: 'demo',
          checkboxGroup: ['apple', 'pear'],
          radioGroup: 'apple',
          radioGroup2: 'apple',
          treeSelect: 'bamboo',
          cascader: ['zhejiang', 'hangzhou'],
          datePicker: moment(new Date()),
          datePicker2: moment(new Date()),
          rangePicker: [moment(new Date()).subtract(7, 'days'), moment(new Date())],
          monthPicker: moment(),
          weekPicker: moment(),
          timePicker: moment('13:30:56', 'HH:mm:ss'),
          inputNumber: 88,
          textArea: 'Hello Input.TextArea:)',
        }}
      >
        <Form.Item label="Chekbox" name="disabled" valuePropName="checked">
          <Checkbox>Checkbox</Checkbox>
        </Form.Item>
        <Form.Item label="Radio" name="radioGroup">
          <Radio.Group>
            <Radio value="apple"> Apple </Radio>
            <Radio value="pear"> Pear </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Radio" name="radioGroup2">
          <Radio.Group options={options}>
            <Radio value="apple"> Apple </Radio>
            <Radio value="pear"> Pear </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Input" name="input">
          <Input ref={inputRef} />
        </Form.Item>
        <Form.Item label="ChekboxGroup" name="checkboxGroup">
          <Checkbox.Group options={options} />
        </Form.Item>
        <Form.Item label="Select" name="select">
          <Select>
            <Select.Option value="demo">Demo</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="Select" name="select2">
          <Select options={[{ label: 'Demo', value: 'demo' }]}>
            <Select.Option value="demo">Demo</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item label="TreeSelect" name="treeSelect">
          <TreeSelect
            treeData={[
              { title: 'Light', value: 'light', children: [{ title: 'Bamboo', value: 'bamboo' }] },
            ]}
            treeDefaultExpandAll
          />
        </Form.Item>
        <Form.Item label="Cascader" name="cascader">
          <Cascader
            options={[
              {
                value: 'zhejiang',
                label: 'Zhejiang',
                children: [
                  {
                    value: 'hangzhou',
                    label: 'Hangzhou',
                  },
                ],
              },
            ]}
          />
        </Form.Item>
        <Form.Item label="DatePicker" name="datePicker">
          <DatePicker />
        </Form.Item>
        <Form.Item label="DatePicker2" name="datePicker2">
          <DatePicker showTime />
        </Form.Item>
        <Form.Item label="RangePicker" name="rangePicker">
          <RangePicker />
        </Form.Item>
        <Form.Item label="MonthPicker" name="monthPicker">
          <MonthPicker />
        </Form.Item>
        <Form.Item label="WeekPicker" name="weekPicker">
          <WeekPicker />
        </Form.Item>
        <Form.Item label="TimePicker" name="timePicker">
          <TimePicker />
        </Form.Item>
        <Form.Item label="InputNumber" name="inputNumber">
          <InputNumber />
        </Form.Item>
        <Form.Item label="TextArea" name="textArea">
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item label="Switch" name="switch" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Upload" name="upload">
          <Upload listType="picture-card" beforeUpload={() => false}>
            <div>
              <Icon type="plus" />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
        </Form.Item>
        <Form.Item label="Button">
          <Button>Button</Button>
        </Form.Item>
      </Form>
    </>
  )
}

export const FormDisabledReadonly = FormDisabledTpl.bind({})

// 嵌套结构与校验信息
const NestValidatorFormTpl = () => {
  const layout = {
    labelCol: { span: 2 },
    wrapperCol: { span: 12 },
  }

  /* eslint-disable no-template-curly-in-string */
  const validateMessages = {
    required: '${label} is required!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  }
  const onFinish = (values: any) => {
    console.log(values)
  }

  return (
    <Form {...layout} name="nest-messages" onFinish={onFinish} validateMessages={validateMessages}>
      <Form.Item name={['user', 'name']} label="Name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name={['user', 'email']} label="Email" rules={[{ type: 'email' }]}>
        <Input />
      </Form.Item>
      <Form.Item name={['user', 'age']} label="Age" rules={[{ type: 'number', min: 0, max: 99 }]}>
        <InputNumber />
      </Form.Item>
      <Form.Item name={['user', 'website']} label="Website">
        <Input />
      </Form.Item>
      <Form.Item name={['user', 'introduction']} label="Introduction">
        <Input.TextArea />
      </Form.Item>
      <Form.Item wrapperCol={{ ...layout.wrapperCol, offset: 8 }}>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

export const NestValidatorForm = NestValidatorFormTpl.bind({})
